import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AdminClientSummary } from '../index.js'
import { EndpointHint, Switch } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = Pick<ClientsAdminApi, 'updateViewAs'>

/**
 * The admin's per-client switch for View as…: whether the client's own
 * operators and facility administrators may open a demo session as someone
 * below them. Off by default; the admin's own Open as… works either way.
 */
export function ClientViewAsSwitch({ api, client }: { api: AdminApi; client: AdminClientSummary }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (staffViewAs: boolean) => api.updateViewAs(client.registrationId, staffViewAs),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] }),
  })
  const checked = mutation.isPending ? mutation.variables === true : client.staffViewAs === true

  return (
    <div>
      <div className="flex items-center gap-3">
        <Switch checked={checked} onChange={(next) => mutation.mutate(next)} label="Staff may use View as" disabled={mutation.isPending} />
        <span className="text-xs text-ink">Operators and facility administrators may use View as…</span>
        <EndpointHint>PATCH /admin/m2m/registrations/{'{registrationId}'}/view-as</EndpointHint>
      </div>
      <p className="text-[11px] text-ink-mid mt-1">
        Off by default. When off, only a SeeWhoCares admin opens this demo as someone else; a session the client's staff opened stops switching too.
      </p>
      {mutation.isError && <p className="text-[11px] text-coral mt-1">{mutation.error instanceof Error ? mutation.error.message : 'Could not change the switch.'}</p>}
    </div>
  )
}
